import Button from "components/ui/Button";
import StarIcon from "images/star.svg?react";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDocumentTitle } from "utils/useDocumentTitle";
import AppLanguageSwitcher from "../../components/AppLanguageSwitcher";
import ArrowLeft from "./../../images/arrow_left.svg?react";
import MainLogo from "./../../images/main-logo.svg?react";

import { WEBSITE_ROUTES } from "pages/routes";
import { useParams, useSearchParams } from "react-router-dom";
import EmailSignUp from "./components/EmailSignUp/EmailSignUp";
import SelectMethod from "./components/SelectMethod/SelectMethod";
import VerifyEmail from "./components/VerifyEmail/VerifyEmail";
import styles from "./SelfSignUp.module.scss";

export type SignUpLocationState = {
  returnPath?: string;
};

export type SignUpRouteParams = { code?: string };

type Steps = "SelectMethod" | "EmailSignUp" | "VerifyNotification";

function SelfSignUp() {
  const { t } = useTranslation();

  const studentType = useSearchParams()[0].get("oneOnOne") ? "one-on-one" : "self-study";
  const pricingModelCode = useParams<SignUpRouteParams>().code;

  const [email, setEmail] = useState("");
  const [step, setStep] = useState<Steps>("SelectMethod");
  const [resendVerificationToken, setResendVerificationToken] = useState<string>();

  useDocumentTitle(
    t("Auth.signUpPage.pageTitle"),
    "This page is for guest to sign up as new user"
  );

  const onEmailAccountCreated = (userEmail: string, token: string) => {
    setEmail(userEmail);
    setResendVerificationToken(token);
    setStep("VerifyNotification");
  };

  const onEmailMethodSelected = () => {
    setStep("EmailSignUp");
  };

  const onPreviousStep = () => {
    setStep("SelectMethod");
  };

  return (
    <div className={styles.selfSignUpPage}>
      <header className={styles.header}>
        {step !== "SelectMethod" && (
          <Button className={styles.previousStep} onClick={onPreviousStep}>
            <ArrowLeft />
          </Button>
        )}
        <a href={WEBSITE_ROUTES.home} className={styles.logoLink}>
          <MainLogo />
        </a>
        <AppLanguageSwitcher color="black" className={styles.languageBtn} />
      </header>

      <main className={styles.pageContent}>
        {step === "SelectMethod" ? (
          <SelectMethod
            onEmailSelected={onEmailMethodSelected}
            studentType={studentType}
            pricingModelCode={pricingModelCode}
          />
        ) : step === "EmailSignUp" ? (
          <EmailSignUp
            studentType={studentType}
            pricingModelCode={pricingModelCode}
            onSignUp={onEmailAccountCreated}
          />
        ) : (
          <VerifyEmail email={email} token={resendVerificationToken!} />
        )}

        {step !== "VerifyNotification" && (
          <>
            <p className={styles.legalPanel}>
              <Trans
                i18nKey={"Auth.signUpPage.legalNotice"}
                components={{
                  privacy: (
                    <a
                      href={WEBSITE_ROUTES.privacyPolicy}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                  terms: (
                    <a
                      href={WEBSITE_ROUTES.termsOfService}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                }}
              />
            </p>

            <div className={styles.testimonial}>
              <div className={styles.rating}>
                <StarIcon />
                <StarIcon />
                <StarIcon />
                <StarIcon />
                <StarIcon />
              </div>

              {t(`Auth.CreateAccount.testimonial.${studentType}`)}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default SelfSignUp;
